// The value model behind the unified form renderer. One FormState drives the
// builder's test mode, the public form page, and payload building, so what the
// builder shows is — by construction — what the published form does.
import type { FieldDefinition, FormLayout, FormRule, FormWidget, WidgetNode } from '../shared/types';
import { flattenWidgetTree } from '../shared/types';
import type { FormConfig } from '../shared/types';
import { fieldDefault } from './config';

/** Everything the renderer needs about a published (or draft-assembled) form. */
export interface PublishedDef {
  fields: FieldDefinition[];
  layout: FormLayout;
  rules: FormRule[];
  widgets: FormWidget[];
  /**
   * The order this form continues, for the rules that name it — the `parent`
   * scope, read as `parent.orderType` or, one dot deeper into a composite,
   * `parent.shipAddress.state`. The form the parent continued rides under
   * its own `parent`, so a rule can read `parent.parent.companyName`.
   *
   * It lives on the DEFINITION rather than on `FormState` because it is
   * ambient and read-only, the way `rules` is: the respondent never typed it
   * and can never change it. That placement is also what keeps it out of two
   * places it must not reach — `draftPayload` saves `state.order`, so a draft
   * in localStorage never holds a copy of the order, and `buildSubmissionData`
   * emits declared fields only, so nothing here rides back on the submission.
   *
   * Narrowed server-side to the paths the rules actually read; see
   * server/src/lib/parentRuleKeys.ts. Absent when this form continues nothing,
   * when it was opened without an invite, or when no rule names the parent —
   * and a rule that then reads `parent.x` weighs a blank, exactly as one
   * naming a key some parents lack always has.
   */
  parent?: Record<string, unknown>;
}

/** One repeatable-widget instance: its own answers + child instances per child scope. */
export interface WidgetInstance {
  /** Stable uid — React key and the touched/files key prefix (survives removals). */
  id: string;
  values: Record<string, unknown>;
  children: Record<string, WidgetInstance[]>;
}

export interface FormState {
  /** scope 'order' answers */
  order: Record<string, unknown>;
  /** root widget scope -> instances */
  widgets: Record<string, WidgetInstance[]>;
  /** `${instanceId}.${fieldKey}` (order scope: just fieldKey) -> user edited it */
  touched: Record<string, boolean>;
  /** same keying as touched -> picked files for 'file' fields */
  files: Record<string, File[]>;
  /**
   * same keying as touched -> HOW a 'signature' field was signed.
   *
   * Beside the answer rather than in it, for the same reason `files` is: the
   * answer is the drawing, and "they typed this name rather than drawing it"
   * is a fact about the drawing. It travels to the server as its own key on
   * the submit payload, where it is narrowed and recorded — it is never
   * allowed to be the answer itself.
   */
  signatures: Record<string, SignatureMode>;
}

/** What the browser can say about how one signature was made. */
export interface SignatureMode {
  mode: 'draw' | 'type';
  /** the name behind a typed signature, as entered */
  typedName?: string;
}

/** Path from the root to an instance: [{scope:'location',index:0},{scope:'device',index:1}] */
export type InstancePath = { scope: string; index: number }[];

let uidCounter = 0;
const uid = () => `wi${Date.now().toString(36)}-${(uidCounter++).toString(36)}`;

/** touched/files key for a field in an instance context ('' path = order scope). */
export const valueKey = (instanceId: string | null, field: string) =>
  instanceId ? `${instanceId}.${field}` : field;

/**
 * The widget a section renders.
 *
 * `widgetId` is the answer whenever the section carries one — including when
 * it names a widget that is gone, which must render NOTHING rather than
 * quietly falling through to somebody else's container. The fallback exists
 * only for legacy `type: 'locations'` sections, which predate the id.
 */
export function widgetForSection(
  widgets: FormWidget[] | undefined, section: { type: string; widgetId?: string }
): FormWidget | undefined {
  const list = widgets || [];
  if (section.widgetId) return list.find((w) => w.id === section.widgetId);
  return list.find((w) => w.id === 'locations') || list.find((w) => w.builtin);
}

/** scope -> widget node, across every widget tree of the form. */
export function nodesByScope(def: PublishedDef): Record<string, WidgetNode> {
  const map: Record<string, WidgetNode> = {};
  for (const w of def.widgets || []) {
    if (w.tree) flattenWidgetTree(w.tree).forEach((n) => { map[n.scope] = n; });
  }
  return map;
}

/** The answers the fields of one scope start from (see config.fieldDefault). */
function scopeDefaults(def: PublishedDef, cfg: FormConfig, scope: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of def.fields) {
    if (f.scope !== scope) continue;
    const d = fieldDefault(f, cfg);
    // `false` is a real default for a checkbox that starts ticked-off; only
    // "there is no default" stays out of the state.
    if (d !== undefined) out[f.key] = d;
  }
  return out;
}

/** A fresh instance of a node, pre-creating `min` child instances recursively. */
export function newInstance(def: PublishedDef, cfg: FormConfig, node: WidgetNode): WidgetInstance {
  const children: Record<string, WidgetInstance[]> = {};
  for (const c of node.children || []) {
    children[c.scope] = Array.from({ length: c.min }, () => newInstance(def, cfg, c));
  }
  return { id: uid(), values: scopeDefaults(def, cfg, node.scope), children };
}

/** Initial state: order-scope defaults + `min` instances of every widget root. */
export function createInitialState(def: PublishedDef, cfg: FormConfig = {}): FormState {
  const widgets: Record<string, WidgetInstance[]> = {};
  for (const w of def.widgets || []) {
    if (!w.tree) continue;
    widgets[w.tree.scope] = Array.from({ length: w.tree.min }, () => newInstance(def, cfg, w.tree));
  }
  return { order: scopeDefaults(def, cfg, 'order'), widgets, touched: {}, files: {}, signatures: {} };
}

/** The instance list holding `scope` under `parentPath` ([] = a root list). */
function resolveList(state: FormState, parentPath: InstancePath, scope: string): WidgetInstance[] | null {
  if (!parentPath.length) return state.widgets[scope] || null;
  let inst: WidgetInstance | undefined;
  for (const seg of parentPath) {
    const list: WidgetInstance[] | undefined = inst ? inst.children[seg.scope] : state.widgets[seg.scope];
    inst = list?.[seg.index];
    if (!inst) return null;
  }
  return inst ? inst.children[scope] || null : null;
}

/**
 * A stored submission's answers as an editable FormState.
 *
 * Scalars go in through `setValue`; a repeating group's rows are rebuilt
 * instance by instance so add/remove behaves exactly as it did for the
 * respondent. Used by the admin drawer's editor and the public edit page
 * (P6), so both hydrate the same way. `touched` and `files` start empty:
 * nothing here was typed just now, and a stored file is a URL, not a File.
 */
export function stateFromData(def: PublishedDef, data: Record<string, unknown>): FormState {
  let next = createInitialState(def, {});
  for (const [key, value] of Object.entries(data || {})) {
    if (Array.isArray(value) && value.every((v) => v && typeof v === 'object')) continue; // widgets below
    next = setValue(next, [], key, value);
  }
  const widgets: FormState['widgets'] = {};
  const walk = (rows: Record<string, unknown>[], node: WidgetNode): WidgetInstance[] =>
    rows.map((row) => {
      const children: Record<string, WidgetInstance[]> = {};
      for (const child of node.children || []) {
        const list = Array.isArray(row[child.scope]) ? (row[child.scope] as Record<string, unknown>[]) : [];
        children[child.scope] = walk(list, child);
      }
      const values: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(row)) {
        if (Array.isArray(v) && v.every((x) => x && typeof x === 'object')) continue;
        values[k] = v;
      }
      return { id: uid(), values, children };
    });
  for (const w of def.widgets || []) {
    if (!w.tree) continue;
    const rows = Array.isArray(data[w.tree.scope]) ? (data[w.tree.scope] as Record<string, unknown>[]) : [];
    widgets[w.tree.scope] = walk(rows, w.tree);
  }
  return { ...next, widgets };
}

export function resolveInstance(state: FormState, path: InstancePath): WidgetInstance | null {
  if (!path.length) return null;
  const last = path[path.length - 1];
  const list = resolveList(state, path.slice(0, -1), last.scope);
  return list?.[last.index] || null;
}

/** Immutably replace the list of `scope` instances under `parentPath`. */
function withList(
  state: FormState, parentPath: InstancePath, scope: string,
  update: (list: WidgetInstance[]) => WidgetInstance[]
): FormState {
  if (!parentPath.length) {
    return { ...state, widgets: { ...state.widgets, [scope]: update(state.widgets[scope] || []) } };
  }
  const [head, ...rest] = parentPath;
  const rootList = state.widgets[head.scope] || [];
  const patchInst = (inst: WidgetInstance, remaining: InstancePath): WidgetInstance => {
    if (!remaining.length) {
      return { ...inst, children: { ...inst.children, [scope]: update(inst.children[scope] || []) } };
    }
    const [seg, ...tail] = remaining;
    const list = inst.children[seg.scope] || [];
    return {
      ...inst,
      children: {
        ...inst.children,
        [seg.scope]: list.map((c, i) => (i === seg.index ? patchInst(c, tail) : c)),
      },
    };
  };
  return {
    ...state,
    widgets: {
      ...state.widgets,
      [head.scope]: rootList.map((c, i) => (i === head.index ? patchInst(c, rest) : c)),
    },
  };
}

/** Add an instance of `scope` under `parentPath`; no-op at the node's max (0 = unlimited). */
export function addInstance(state: FormState, def: PublishedDef, cfg: FormConfig, parentPath: InstancePath, scope: string): FormState {
  const node = nodesByScope(def)[scope];
  if (!node) return state;
  const list = resolveList(state, parentPath, scope) || [];
  if (node.max > 0 && list.length >= node.max) return state;
  return withList(state, parentPath, scope, (l) => [...l, newInstance(def, cfg, node)]);
}

/** Remove the instance at `path`; no-op at the node's min. */
export function removeInstance(state: FormState, def: PublishedDef, path: InstancePath): FormState {
  if (!path.length) return state;
  const last = path[path.length - 1];
  const node = nodesByScope(def)[last.scope];
  if (!node) return state;
  const list = resolveList(state, path.slice(0, -1), last.scope) || [];
  if (list.length <= node.min) return state;
  return withList(state, path.slice(0, -1), last.scope, (l) => l.filter((_, i) => i !== last.index));
}

/** Set an answer in the order scope or inside an instance; marks it touched. */
export function setValue(state: FormState, path: InstancePath, key: string, v: unknown): FormState {
  if (!path.length) {
    return {
      ...state,
      order: { ...state.order, [key]: v },
      touched: { ...state.touched, [key]: true },
    };
  }
  const inst = resolveInstance(state, path);
  if (!inst) return state;
  const last = path[path.length - 1];
  const next = withList(state, path.slice(0, -1), last.scope, (l) =>
    l.map((c, i) => (i === last.index ? { ...c, values: { ...c.values, [key]: v } } : c))
  );
  return { ...next, touched: { ...next.touched, [valueKey(inst.id, key)]: true } };
}

/** Record how a 'signature' field was signed, in any context. */
export function setSignatureMode(
  state: FormState, instanceId: string | null, key: string, mode: SignatureMode
): FormState {
  return { ...state, signatures: { ...state.signatures, [valueKey(instanceId, key)]: mode } };
}

/** Set the picked files for a 'file' field in any context. */
export function setFiles(state: FormState, instanceId: string | null, key: string, files: File[]): FormState {
  return { ...state, files: { ...state.files, [valueKey(instanceId, key)]: files } };
}

/**
 * Counts of every DESCENDANT scope beneath one instance, keyed by that node's
 * countKey. A location instance therefore reports its own device count, its own
 * contact count, AND the total lines across all of its devices.
 */
export function instanceCounts(inst: WidgetInstance, node: WidgetNode): Record<string, number> {
  const out: Record<string, number> = {};
  const walk = (i: WidgetInstance, n: WidgetNode) => {
    for (const child of n.children || []) {
      const list = i.children[child.scope] || [];
      out[child.countKey] = (out[child.countKey] || 0) + list.length;
      list.forEach((c) => walk(c, child));
    }
  };
  walk(inst, node);
  return out;
}

/** Instance counts per scope, summed across parent instances (feeds count calcs). */
export function scopeCounts(state: FormState, def: PublishedDef): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const w of def.widgets || []) {
    if (w.tree) flattenWidgetTree(w.tree).forEach((n) => { counts[n.scope] = 0; });
  }
  const walk = (list: WidgetInstance[], scope: string) => {
    counts[scope] = (counts[scope] || 0) + list.length;
    for (const inst of list) {
      for (const [childScope, childList] of Object.entries(inst.children)) walk(childList, childScope);
    }
  };
  for (const [scope, list] of Object.entries(state.widgets)) walk(list, scope);
  return counts;
}
